/**
 * group 'company': a member of the active company (brand) at that tier.
 * group 'self':    someone with no company (self-use) at that tier: the RiskLo
 *                  home look, the welcome Overview, no company strategies.
 */
export const ADMIN_VIEW_OPTIONS = [
  { id: 'basic', label: 'Basic', group: 'company' },
  { id: 'pro', label: 'Pro', group: 'company' },
  { id: 'max', label: 'Max', group: 'company' },
  { id: 'sales', label: 'Sales', group: 'company' },
  { id: 'support', label: 'Support', group: 'company' },
  { id: 'vadmin', label: 'Brand Admin', group: 'company' },
  { id: 'vadminPlus', label: 'Brand Admin Plus', group: 'company' },
  { id: 'admin', label: 'Platform Admin', group: 'company' },
  { id: 'selfBasic', label: 'Basic', group: 'self', tier: 'basic' },
  { id: 'selfPro', label: 'Pro', group: 'self', tier: 'pro' },
  { id: 'selfMax', label: 'Max', group: 'self', tier: 'max' },
];

const SELF_VIEW_IDS = new Set(ADMIN_VIEW_OPTIONS.filter((o) => o.group === 'self').map((o) => o.id));

/** Previewing someone with no company (self-use Basic / Pro / Max). */
export function isSelfUseView(viewAs) {
  return SELF_VIEW_IDS.has(viewAs);
}

const ADMIN_VIEW_IDS = new Set(ADMIN_VIEW_OPTIONS.map((o) => o.id));
const STORAGE_PREFIX = 'risklo_admin_view_as';

/** Member-facing tiers — internal strategies must not appear (matches server). */
export const PREVIEW_HIDES_INTERNAL = new Set(['basic', 'pro', 'max', 'sales', 'support', 'selfBasic', 'selfPro', 'selfMax']);

export function adminViewStorageKey(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return normalized ? `${STORAGE_PREFIX}_${normalized}` : null;
}

export function readAdminViewAs(email) {
  const key = adminViewStorageKey(email);
  if (!key || typeof localStorage === 'undefined') return 'admin';
  try {
    const raw = localStorage.getItem(key);
    if (ADMIN_VIEW_IDS.has(raw)) return raw;
  } catch (_err) {
    // ignore
  }
  return 'admin';
}

export function writeAdminViewAs(email, viewAs) {
  const key = adminViewStorageKey(email);
  if (!key || !ADMIN_VIEW_IDS.has(viewAs)) return;
  try {
    localStorage.setItem(key, viewAs);
  } catch (_err) {
    // ignore
  }
}

export function clearAdminViewAs(email) {
  const key = adminViewStorageKey(email);
  if (!key || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch (_err) {
    // ignore
  }
}

function withProIncluded(tiers) {
  return {
    ...tiers,
    isPro: Boolean(tiers.isPro || tiers.isMax || tiers.isAdmin),
  };
}

/** Apply admin-only UI preview tier; server still treats user as admin. */
export function applyAdminViewAs(serverTiers, viewAs, isAdminActual) {
  const server = serverTiers || {};
  if (!isAdminActual || viewAs === 'admin') {
    return withProIncluded({
      isAdmin: Boolean(server.isAdmin),
      isVectorAdmin: Boolean(server.isVectorAdmin),
      isVectorAdminPlus: Boolean(server.isVectorAdminPlus),
      isSupport: Boolean(server.isSupport),
      isSales: Boolean(server.isSales),
      isMax: Boolean(server.isMax),
      isPro: Boolean(server.isPro),
      isBeta: Boolean(server.isBeta),
      isDevMode: Boolean(server.isDevMode),
    });
  }

  const previewBase = {
    isAdmin: false,
    isVectorAdminPlus: false,
    isSupport: false,
    isSales: false,
    isBeta: false,
    isDevMode: false,
  };

  const tierView = ADMIN_VIEW_OPTIONS.find((o) => o.id === viewAs)?.tier || viewAs;
  switch (tierView) {
    case 'vadminPlus':
      return { ...previewBase, isVectorAdmin: true, isVectorAdminPlus: true, isMax: true, isPro: true };
    case 'vadmin':
      return { ...previewBase, isVectorAdmin: true, isMax: true, isPro: true };
    case 'support':
      return { ...previewBase, isVectorAdmin: false, isSupport: true, isMax: true, isPro: true };
    case 'sales':
      return { ...previewBase, isVectorAdmin: false, isSales: true, isMax: true, isPro: true };
    case 'max':
      return { ...previewBase, isVectorAdmin: false, isMax: true, isPro: true };
    case 'pro':
      return { ...previewBase, isVectorAdmin: false, isMax: false, isPro: true };
    case 'basic':
      return { ...previewBase, isVectorAdmin: false, isMax: false, isPro: false };
    default:
      return withProIncluded({
        isAdmin: Boolean(server.isAdmin),
        isVectorAdmin: Boolean(server.isVectorAdmin),
        isVectorAdminPlus: Boolean(server.isVectorAdminPlus),
        isSupport: Boolean(server.isSupport),
        isSales: Boolean(server.isSales),
        isMax: Boolean(server.isMax),
        isPro: Boolean(server.isPro),
        isBeta: Boolean(server.isBeta),
        isDevMode: Boolean(server.isDevMode),
      });
  }
}

export function adminViewLabel(viewAs) {
  const option = ADMIN_VIEW_OPTIONS.find((o) => o.id === viewAs);
  if (!option) return 'Admin';
  return option.group === 'self' ? `Self-use ${option.label}` : option.label;
}

/** Removed from nav; direct URLs redirect home (routes kept for future use). */
export const HIDDEN_ADMIN_PAGES = [
  'admin-feedback',
  'admin-vector-nt-mappings',
  'overview-test',
];

export const VECTOR_ADMIN_PAGES = [
  'admin-sheet-manager',
  'admin-live-strategy',
  'admin-equity-strategies',
  'admin-scenario-data',
  'admin-nt8-webhook-mapping',
  'admin-users',
  'admin-logs',
  'admin-ai',
];

/** Admin preview tiers must leave these routes (see App.js redirect). */
export function adminPagesBlockedInPreview(viewAs) {
  if (viewAs === 'admin' || viewAs === 'vadmin' || viewAs === 'vadminPlus') return [];
  return VECTOR_ADMIN_PAGES;
}

/** Whether Results should list internal strategies (admin / vadmin preview only). */
export function resultsAudienceIncludesInternal(isAdminActual, adminViewAs) {
  if (!isAdminActual) return false;
  if (PREVIEW_HIDES_INTERNAL.has(adminViewAs)) return false;
  return true;
}

/** Cache/API audience segment for /api/sheets and portfolio analytics. */
export function sheetsAudienceCacheKey(isAdminActual, adminViewAs) {
  if (!isAdminActual) return 'member';
  if (PREVIEW_HIDES_INTERNAL.has(adminViewAs)) return adminViewAs;
  if (adminViewAs === 'admin' || adminViewAs === 'vadmin' || adminViewAs === 'vadminPlus') return 'elevated';
  return 'member';
}

/** Users page lists every staff admin may add people to (matches server). */
const STAFF_ADMIN_EDITABLE_LIST_KEYS = ['SUPPORT_EMAILS', 'SALES_EMAILS'];

/**
 * Users page while an Admin previews a lower tier. The server still answers as
 * Admin (it can't know about the preview), so the page applies the previewed
 * tier's rules itself: lists the tier must not see are hidden, Brand Admin can
 * only add to Support and Sales, Brand Admin Plus can add/edit everything but
 * the Admin list, and neither can remove. Returns null when not previewing (use
 * the server's answer as-is).
 */
export function usersPagePreviewRules(viewAs, isAdminActual) {
  if (!isAdminActual || !viewAs || viewAs === 'admin') return null;
  if (viewAs === 'vadminPlus') {
    return {
      hiddenListKeys: ['ADMIN_EMAILS'],
      canEditList: (key) => key !== 'ADMIN_EMAILS',
      canRemove: false,
    };
  }
  // Brand Admin (and anything lower, which cannot open the page anyway).
  // Dev lists are for Admin and Brand Admin Plus only (matches server).
  return {
    hiddenListKeys: [
      'ADMIN_EMAILS', 'VECTOR_ADMIN_PLUS_EMAILS',
      'DEV_MODE_MAX_EMAILS', 'DEV_MODE_PRO_EMAILS', 'DEV_MODE_BASIC_EMAILS',
    ],
    canEditList: (key) => STAFF_ADMIN_EDITABLE_LIST_KEYS.includes(key),
    canRemove: false,
  };
}
